import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Alert,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Code, Language, Add } from "@mui/icons-material";
import React, { useState } from "react";

const SkillsCertsStep = ({
  profileData,
  handleInputChange,
  handleAddItem,
  handleDeleteItem,
  currentFrameworkTool,
  setCurrentFrameworkTool,
  initialFrameworkToolState,
  currentCertification,
  setCurrentCertification,
  initialCertificationState,
  currentLanguage,
  setCurrentLanguage,
  currentLangProficiency,
  setCurrentLangProficiency,
  handleLanguageAdd,
  handleLanguageRemove,
  showNotification,
  DynamicListSection,
  skillOptions,
  languageOptions,
  proficiencyOptions,
  validationErrors,
}) => {
  const theme = useTheme(); // CHANGED: Instantiate theme
  const [skillInput, setSkillInput] = useState("");

  // CHANGED: Removed all local style objects (paperStyles, sectionTitleStyles, etc.)

  const handleAddSkills = () => {
    const newSkills = skillInput
      .split(",")
      .map((skill) => skill.trim())
      .filter((skill) => skill && !profileData.skills.includes(skill));

    if (newSkills.length > 0) {
      handleInputChange("skills", [...profileData.skills, ...newSkills]);
    }
    setSkillInput("");
  };

  const handleAddFrameworks = () => {
    if (!currentFrameworkTool.name) return;
    const tools = currentFrameworkTool.name
      .split(",")
      .map((tool) => tool.trim())
      .filter(Boolean);
    tools.forEach((toolName) => {
      handleAddItem("frameworks_tools", { name: toolName });
    });
    setCurrentFrameworkTool(initialFrameworkToolState);
  };

  const handleAddCertification = () => {
    if (!currentCertification.name || !currentCertification.issuer) {
      showNotification("Certification Name and Issuer are required.", "error");
      return;
    }

    handleAddItem("certification_details", currentCertification);
    showNotification("Certification added successfully!", "success");
    setCurrentCertification(initialCertificationState);
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Highlight your technical skills, certifications, and language
        proficiency. Technical skills are mandatory.
      </Typography>

      {validationErrors.skills && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {validationErrors.skills}
        </Alert>
      )}

      <Stack spacing={4}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "20px",
            bgcolor: "background.paper",
            border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
          }}
        >

          <Typography
            variant="h6"
            gutterBottom
            // CHANGED: Replaced sx object with theme-aware values
            sx={{
              color: "primary.main",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Code /> Technical Skills{" "}
            <span style={{ color: theme.palette.error.main }}>*</span>
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <Autocomplete
                multiple
                id="skills-autocomplete"
                freeSolo
                options={skillOptions.filter(
                  (opt) => !profileData.skills.includes(opt)
                )}
                value={profileData.skills || []}
                onChange={(event, newValue) => {
                  handleInputChange("skills", newValue);
                }}
                onInputChange={(event, newInputValue) => {
                  setSkillInput(newInputValue);
                }}
                inputValue={skillInput}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={option}
                      label={option}
                      color="primary"
                      variant="filled"
                      sx={{ borderRadius: "8px", fontWeight: 500 }}
                      {...getTagProps({ index })}
                    />

                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Add Skills"
                    placeholder="Select or type skills (comma-separated)"
                    // CHANGED: Removed sx prop, styles come from global theme
                    error={!!validationErrors.skills}
                  />
                )}
              />
            </Grid>
            <Grid item>
              <Button
                onClick={handleAddSkills}
                variant="contained"
                color="primary"
                sx={{
                  height: "56px",
                  px: 4,
                  borderRadius: "12px",
                  fontWeight: "bold",
                  boxShadow: theme => `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
                }}
                startIcon={<Add />}
              >
                Add Skills
              </Button>

            </Grid>
          </Grid>
        </Paper>

        <DynamicListSection
          title="Frameworks & Tools"
          field="frameworks_tools"
          items={profileData.frameworks_tools}
          formState={currentFrameworkTool}
          setFormState={setCurrentFrameworkTool}
          initialState={initialFrameworkToolState}
          formFields={[
            {
              name: "name",
              label: "Add Frameworks & Tools (comma-separated)",
            },
          ]}
          renderItem={(item) => (
            <Box>
              <Typography variant="subtitle2" sx={{ color: "primary.main" }}>
                {item.name}
              </Typography>
            </Box>
          )}
          handleAddItem={handleAddFrameworks}
          handleDeleteItem={handleDeleteItem}
          showNotification={showNotification}
        />

        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "20px",
            bgcolor: "background.paper",
            border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
          }}
        >

          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: "primary.main",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            Development Environment
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Preferred Tech Stack"
                helperText="e.g., Python, FastAPI, React"
                value={profileData.preferred_tech_stack}
                onChange={(e) =>
                  handleInputChange("preferred_tech_stack", e.target.value)
                }
                variant="outlined"
                // CHANGED: Removed sx prop
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Development Environment"
                helperText="e.g., VS Code, Jupyter, PyCharm"
                value={profileData.dev_environment}
                onChange={(e) =>
                  handleInputChange("dev_environment", e.target.value)
                }
                variant="outlined"
                // CHANGED: Removed sx prop
              />
            </Grid>
          </Grid>
        </Paper>

        <DynamicListSection
          title="Certifications"
          field="certification_details"
          items={profileData.certification_details}
          formState={currentCertification}
          setFormState={setCurrentCertification}
          initialState={initialCertificationState}
          formFields={[
            { name: "name", label: "Certification Name" },
            { name: "issuer", label: "Issuer Name" },
            { name: "date", label: "Issue Date", placeholder: "YYYY-MM-DD" },
          ]}
          renderItem={(item, index) => (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs>
                <Typography variant="subtitle2" sx={{ color: "primary.main" }}>
                  {item.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {`${item.issuer} ${item.date ? `(${item.date})` : ""}`}
                </Typography>
              </Grid>
            </Grid>
          )}
          handleAddItem={handleAddCertification}
          handleDeleteItem={handleDeleteItem}
          showNotification={showNotification}
        />

        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "20px",
            bgcolor: "background.paper",
            border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
          }}
        >

          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: "primary.main",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Language /> Language Proficiency
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              mb: 2,
              p: 1,
              borderRadius: 1,
              minHeight: "52px",
            }}
          >
            {Object.entries(profileData.languages).map(([name, level]) => (
              <Chip
                key={name}
                label={`${name} (${level})`}
                onDelete={() => handleLanguageRemove(name)}
                color="primary"
                sx={{ borderRadius: "8px", fontWeight: 500 }}
              />

            ))}
            {Object.keys(profileData.languages).length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ width: "100%", textAlign: "center", p: 1 }}
              >
                No languages added yet.
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <FormControl fullWidth>
              <InputLabel>Language</InputLabel>
              <Select
                value={currentLanguage}
                onChange={(e) => setCurrentLanguage(e.target.value)}
                label="Language"
                // CHANGED: Removed sx prop
              >
                {languageOptions.map((l) => (
                  <MenuItem key={l} value={l}>
                    {l}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Proficiency</InputLabel>
              <Select
                value={currentLangProficiency}
                onChange={(e) => setCurrentLangProficiency(e.target.value)}
                label="Proficiency"
                // CHANGED: Removed sx prop
              >
                {proficiencyOptions.map((l) => (
                  <MenuItem key={l} value={l}>
                    {l}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              onClick={handleLanguageAdd}
              variant="contained"
              color="primary"
              sx={{
                minWidth: "180px",
                height: "56px",
                mt: { xs: 1, sm: 0 },
                borderRadius: "12px",
                fontWeight: "bold",
                boxShadow: theme => `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
              startIcon={<Add />}
            >
              Add Language
            </Button>

          </Box>
        </Paper>
      </Stack>
    </Box>
  );
};

export default SkillsCertsStep;
